import { useEffect, useRef, useState } from 'react';

function useCountUp(target: number, active: boolean, duration = 1000, delay = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf: number;
    let timeout: number;
    const run = () => {
      const start = performance.now();
      const step = (now: number) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setValue(target * eased);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    };
    timeout = window.setTimeout(run, delay);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [target, active, duration, delay]);
  return value;
}

export default function Impact() {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          setActive(true);
          obs.disconnect();
        }
      }),
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const co2 = useCountUp(14.2, active, 1000, 0);
  const kwh = useCountUp(18900, active, 1100, 120);
  const yrs = useCountUp(25, active, 900, 240);

  const revealBase = 'transition-all duration-700 ease-out';
  const revealCls = (i: number) =>
    `${revealBase} ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`;
  const revealStyle = (i: number) => ({ transitionDelay: active ? `${i * 120}ms` : '0ms' });

  return (
    <section ref={ref} className="py-[72px] md:py-[144px] bg-solar-bg">
      <div className="container-x">
        <div className="grid grid-cols-12 gap-6 mb-10 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <p className="text-xs font-medium text-solar-green tracking-wider uppercase">Energy Impact</p>
            <h2 className="display-font mt-4 text-[34px] md:text-[64px] font-semibold leading-[1.02] text-solar-ink max-w-[900px]">
              A small change on the roof. A measurable change in the atmosphere.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 items-start">
          {[
            { value: <>{co2.toFixed(1)}<span className="text-[26px] md:text-[32px] text-solar-muted font-normal ml-1.5">tons</span></>, copy: 'Average CO₂ avoided annually per residential Sunfield system.' },
            { value: <>{Math.round(kwh).toLocaleString()}<span className="text-[26px] md:text-[32px] text-solar-muted font-normal ml-1.5">kWh</span></>, copy: 'Clean electricity generated each year by an average commercial installation.' },
            { value: <>{Math.round(yrs)}<span className="text-[26px] md:text-[32px] text-solar-muted font-normal ml-1.5">yrs</span></>, copy: 'Backed system performance horizon, with real-time monitoring included.' },
          ].map((m, i) => (
            <div key={i} className={`flex flex-col items-start text-left w-full ${revealCls(i)}`} style={revealStyle(i)}>
              <div className="display-font text-[48px] md:text-[88px] font-semibold text-solar-ink leading-none tabular-nums">
                {m.value}
              </div>
              <p className="mt-5 text-solar-muted text-[14px] leading-relaxed">
                {m.copy}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-14 md:mt-24 grid grid-cols-12 gap-6 items-center pt-10 md:pt-12 border-t border-solar-divider">
          <div className="col-span-12 md:col-span-6">
            <p className="display-font text-[24px] md:text-[32px] leading-snug text-solar-ink max-w-[520px]">
              Over 42,000 systems installed. Every one of them still generating quietly, every sunny day.
            </p>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <div className="grid grid-cols-3 rounded-xl border border-solar-divider/70 bg-white/40 divide-x divide-solar-divider/60 px-2 py-5 text-solar-muted text-xs">
              <div className="flex flex-col items-center text-center px-3">
                <div className="display-font text-solar-ink text-[28px] font-semibold leading-none">A+</div>
                <p className="mt-2">BBB rating</p>
              </div>
              <div className="flex flex-col items-center text-center px-3">
                <div className="display-font text-solar-ink text-[28px] font-semibold leading-none">NABCEP</div>
                <p className="mt-2">Certified installers</p>
              </div>
              <div className="flex flex-col items-center text-center px-3">
                <div className="display-font text-solar-ink text-[28px] font-semibold leading-none">4.9<span className="text-solar-yellow">★</span></div>
                <p className="mt-2">Customer rating</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}